import {Source} from "@amplitude/experiment-js-client";
import {add, experiment, flush, getDeviceId, getUserId, Identify, identify, initAll, reset, setUserId, Types} from "@amplitude/unified";
import type {UnifiedOptions} from "@amplitude/unified/lib/cjs/unified";

import {IAmplitudeExperimentVariants} from "../types/IAmplitudeExperimentVariants";
import {defaultAmplitudeLogger, IAmplitudeLogger} from "../types/IAmplitudeLogger";

const amplitudeState = {
    isInitialized: false,
    initializationPromise: null as Promise<void> | null,
    pendingUserId: undefined as string | undefined,
    hasPendingUserId: false,
    pendingUserProperties: null as Record<string, Types.ValidPropertyType> | null
};

const applyAmplitudeUserId = (userId: string | undefined): void => setUserId(userId);

const applyAmplitudeUserProperties = (properties: Record<string, Types.ValidPropertyType>): void => {
    const identifyObj = new Identify();

    for (const [key, value] of Object.entries(properties)) {
        identifyObj.set(key, value);
    }

    identify(identifyObj);
};

const flushPendingAmplitudeUserState = (): void => {
    if (!amplitudeState.isInitialized) return;

    if (amplitudeState.hasPendingUserId) {
        applyAmplitudeUserId(amplitudeState.pendingUserId);
        amplitudeState.hasPendingUserId = false;
    }

    if (amplitudeState.pendingUserProperties) {
        applyAmplitudeUserProperties(amplitudeState.pendingUserProperties);
        amplitudeState.pendingUserProperties = null;
    }
};

const ALLOWED_AMPLITUDE_ENVIRONMENTS = ["production", "stage:top", "stage:dev", "stage:ovh", "local"] as const;
const APP_ENVIRONMENT_PROPERTY = "app_environment";
const AMPLITUDE_ENVIRONMENT_PLUGIN_NAME = "app-environment-enrichment";

type TAmplitudeEnvironment = (typeof ALLOWED_AMPLITUDE_ENVIRONMENTS)[number];

interface IInitAmplitudeParams {
    amplitudeApiKey?: string;
    deploymentKey?: string;
    initialVariants?: IAmplitudeExperimentVariants;
    userId?: string;
    isBrowser?: boolean;
    logger?: IAmplitudeLogger;
    environment?: string;
}

const validateAmplitudeEnvironment = (environment: string): TAmplitudeEnvironment => {
    if (ALLOWED_AMPLITUDE_ENVIRONMENTS.includes(environment as TAmplitudeEnvironment)) {
        return environment as TAmplitudeEnvironment;
    }

    throw new Error(`Invalid Amplitude environment "${environment}". Allowed values: ${ALLOWED_AMPLITUDE_ENVIRONMENTS.join(", ")}`);
};

export const initAmplitude = ({
    amplitudeApiKey,
    deploymentKey,
    initialVariants,
    userId,
    isBrowser = false,
    logger: _logger = defaultAmplitudeLogger,
    environment
}: IInitAmplitudeParams): void => {
    if (!amplitudeApiKey) return; // no key, silent exit

    if (process.env.EXEC_ENV !== "browser" && !isBrowser) return;

    // initialize only once
    if (amplitudeState.isInitialized || amplitudeState.initializationPromise) return;

    const validatedEnvironment = environment ? validateAmplitudeEnvironment(environment) : undefined;
    const options: UnifiedOptions = {
        serverZone: "EU",
        analytics: {
            autocapture: true
        }
    };

    if (deploymentKey) {
        options.experiment = {
            deploymentKey,
            initialVariants: initialVariants ?? {},
            source: Source.InitialVariants
        };
    }

    if (validatedEnvironment) {
        const environmentPlugin: Parameters<typeof add>[0] = {
            name: AMPLITUDE_ENVIRONMENT_PLUGIN_NAME,
            type: "enrichment",
            async execute(event) {
                event.event_properties = {
                    ...(event.event_properties ?? {}),
                    [APP_ENVIRONMENT_PROPERTY]: validatedEnvironment
                };

                return event;
            }
        };

        add(environmentPlugin);
    }

    amplitudeState.initializationPromise = Promise.resolve(initAll(amplitudeApiKey, options))
        .then(() => {
            amplitudeState.isInitialized = true;
            flushPendingAmplitudeUserState();
        })
        .catch((error) => {
            amplitudeState.initializationPromise = null;
            throw error;
        });

    if (userId) setAmplitudeUserId(userId);

    if (validatedEnvironment) {
        const identifyObj = new Identify();

        identifyObj.set(APP_ENVIRONMENT_PROPERTY, validatedEnvironment);
        identify(identifyObj);
    }
};

export const waitForAmplitudeInitialization = async (): Promise<void> => {
    await amplitudeState.initializationPromise;
};

export const setAmplitudeUserId = (userId: string | undefined): void => {
    amplitudeState.pendingUserId = userId;
    amplitudeState.hasPendingUserId = true;

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserId(userId);
        amplitudeState.hasPendingUserId = false;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserId(userId);
};

export const setAmplitudeUserProperties = (properties: Record<string, Types.ValidPropertyType>): void => {
    amplitudeState.pendingUserProperties = {...(amplitudeState.pendingUserProperties ?? {}), ...properties};

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserProperties(properties);
        amplitudeState.pendingUserProperties = null;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserProperties(properties);
};

export const flushAmplitude = async (): Promise<void> => {
    if (!amplitudeState.isInitialized) return;

    await flush().promise;
};

export const resetAmplitude = (): void => {
    amplitudeState.hasPendingUserId = false;
    amplitudeState.pendingUserProperties = null;

    if (amplitudeState.isInitialized) reset();
};

export const getAmplitudeUserId = (): string | undefined => {
    if (!amplitudeState.isInitialized) return undefined;

    return getUserId() ?? undefined;
};

export const getAmplitudeDeviceId = (): string | undefined => {
    if (!amplitudeState.isInitialized) return undefined;

    return getDeviceId() ?? undefined;
};

export {experiment};
import {Source} from "@amplitude/experiment-js-client";
import {add, experiment, flush, getDeviceId, getUserId, Identify, identify, initAll, reset, setUserId, Types} from "@amplitude/unified";
import type {UnifiedOptions} from "@amplitude/unified/lib/cjs/unified";

import {IAmplitudeExperimentVariants} from "../types/IAmplitudeExperimentVariants";
import {defaultAmplitudeLogger, IAmplitudeLogger} from "../types/IAmplitudeLogger";

const amplitudeState = {
    isInitialized: false,
    initializationPromise: null as Promise<void> | null,
    pendingUserId: undefined as string | undefined,
    hasPendingUserId: false,
    pendingUserProperties: null as Record<string, Types.ValidPropertyType> | null
};

const applyAmplitudeUserId = (userId: string | undefined): void => setUserId(userId);

const applyAmplitudeUserProperties = (properties: Record<string, Types.ValidPropertyType>): void => {
    const identifyObj = new Identify();

    for (const [key, value] of Object.entries(properties)) {
        identifyObj.set(key, value);
    }

    identify(identifyObj);
};

const flushPendingAmplitudeUserState = (): void => {
    if (!amplitudeState.isInitialized) return;

    if (amplitudeState.hasPendingUserId) {
        applyAmplitudeUserId(amplitudeState.pendingUserId);
        amplitudeState.hasPendingUserId = false;
    }

    if (amplitudeState.pendingUserProperties) {
        applyAmplitudeUserProperties(amplitudeState.pendingUserProperties);
        amplitudeState.pendingUserProperties = null;
    }
};

const ALLOWED_AMPLITUDE_ENVIRONMENTS = ["production", "stage:top", "stage:dev", "stage:ovh", "local"] as const;
const APP_ENVIRONMENT_PROPERTY = "app_environment";
const AMPLITUDE_ENVIRONMENT_PLUGIN_NAME = "app-environment-enrichment";

type TAmplitudeEnvironment = (typeof ALLOWED_AMPLITUDE_ENVIRONMENTS)[number];

interface IInitAmplitudeParams {
    amplitudeApiKey?: string;
    deploymentKey?: string;
    initialVariants?: IAmplitudeExperimentVariants;
    userId?: string;
    isBrowser?: boolean;
    logger?: IAmplitudeLogger;
    environment?: string;
}

const validateAmplitudeEnvironment = (environment: string): TAmplitudeEnvironment => {
    if (ALLOWED_AMPLITUDE_ENVIRONMENTS.includes(environment as TAmplitudeEnvironment)) {
        return environment as TAmplitudeEnvironment;
    }

    throw new Error(`Invalid Amplitude environment "${environment}". Allowed values: ${ALLOWED_AMPLITUDE_ENVIRONMENTS.join(", ")}`);
};

export const initAmplitude = ({
    amplitudeApiKey,
    deploymentKey,
    initialVariants,
    userId,
    isBrowser = false,
    logger: _logger = defaultAmplitudeLogger,
    environment
}: IInitAmplitudeParams): void => {
    if (!amplitudeApiKey) return; // no key, silent exit

    if (process.env.EXEC_ENV !== "browser" && !isBrowser) return;

    // initialize only once
    if (amplitudeState.isInitialized || amplitudeState.initializationPromise) return;

    const validatedEnvironment = environment ? validateAmplitudeEnvironment(environment) : undefined;
    const options: UnifiedOptions = {
        serverZone: "EU",
        analytics: {
            autocapture: true
        }
    };

    if (deploymentKey) {
        options.experiment = {
            deploymentKey,
            initialVariants: initialVariants ?? {},
            source: Source.InitialVariants
        };
    }

    if (validatedEnvironment) {
        const environmentPlugin: Parameters<typeof add>[0] = {
            name: AMPLITUDE_ENVIRONMENT_PLUGIN_NAME,
            type: "enrichment",
            async execute(event) {
                event.event_properties = {
                    ...(event.event_properties ?? {}),
                    [APP_ENVIRONMENT_PROPERTY]: validatedEnvironment
                };

                return event;
            }
        };

        add(environmentPlugin);
    }

    amplitudeState.initializationPromise = Promise.resolve(initAll(amplitudeApiKey, options))
        .then(() => {
            amplitudeState.isInitialized = true;
            flushPendingAmplitudeUserState();
        })
        .catch((error) => {
            amplitudeState.initializationPromise = null;
            throw error;
        });

    if (userId) setAmplitudeUserId(userId);

    if (validatedEnvironment) {
        const identifyObj = new Identify();

        identifyObj.set(APP_ENVIRONMENT_PROPERTY, validatedEnvironment);
        identify(identifyObj);
    }
};

export const waitForAmplitudeInitialization = async (): Promise<void> => {
    await amplitudeState.initializationPromise;
};

export const setAmplitudeUserId = (userId: string | undefined): void => {
    amplitudeState.pendingUserId = userId;
    amplitudeState.hasPendingUserId = true;

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserId(userId);
        amplitudeState.hasPendingUserId = false;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserId(userId);
};

export const setAmplitudeUserProperties = (properties: Record<string, Types.ValidPropertyType>): void => {
    amplitudeState.pendingUserProperties = {...(amplitudeState.pendingUserProperties ?? {}), ...properties};

    if (amplitudeState.isInitialized) {
        applyAmplitudeUserProperties(properties);
        amplitudeState.pendingUserProperties = null;
        return;
    }

    if (amplitudeState.initializationPromise) applyAmplitudeUserProperties(properties);
};

export const flushAmplitude = async (): Promise<void> => {
    if (!amplitudeState.isInitialized) return;

    await flush().promise;
};

export const resetAmplitude = (): void => {
    amplitudeState.hasPendingUserId = false;
    amplitudeState.pendingUserProperties = null;

    if (amplitudeState.isInitialized) reset();
};

export const getAmplitudeUserId = (): string | undefined => {
    if (!amplitudeState.isInitialized) return undefined;

    return getUserId() ?? undefined;
};

export const getAmplitudeDeviceId = (): string | undefined => {
    if (!amplitudeState.isInitialized) return undefined;

    return getDeviceId() ?? undefined;
};

export {experiment};
